import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert, X } from "lucide-react";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  OVERLAY_CATEGORIES,
  OVERLAY_LABELS,
  type OverlayCategory,
  riskCategoryToOverlay,
} from "@/lib/arch-overlay";
import type { ArchitectureData, Container, DrillLevel, Risk } from "@/lib/arch-parser";
import { renderMarkdown } from "@/lib/arch-parser/parse-markdown";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";
import { LEGACY_ORIGIN_CARD_CLASSES, LegacyOriginChip } from "./LegacyOriginChip";

// Buckets we render below the overlay-aligned ones. These are real Risk
// categories that don't map to an overlay but still deserve a row in the
// sheet. Kept in render order.
const NON_OVERLAY_BUCKETS: Array<{
  key: "operability" | "vendor-lock-in" | "other";
  label: string;
}> = [
  { key: "operability", label: "Operability" },
  { key: "vendor-lock-in", label: "Vendor lock-in" },
  { key: "other", label: "Other" },
];

type BucketKey = OverlayCategory | (typeof NON_OVERLAY_BUCKETS)[number]["key"];

interface RiskEntry {
  bucket: BucketKey;
  bucketLabel: string;
  risk: Risk;
  nodeId: string;
  nodeLabel: string;
  nodeKind: "external-system" | "container" | "component";
}

const RESPONSE_LABEL: Record<Risk["response"]["kind"], string> = {
  mitigation: "Mitigation",
  alternative: "Alternative",
  accepted_because: "Accepted because",
};

// The response answers the risk, so it shouldn't read as alarming red.
// Forward-looking solutions get a positive green tone; accepted risks stay
// neutral. Mirrors RISK_RESPONSE_TONE in DetailPanel.
const RESPONSE_TONE: Record<Risk["response"]["kind"], string> = {
  mitigation: "border-success/40 text-success-ink",
  alternative: "border-success/40 text-success-ink",
  accepted_because: "border-border text-ink-muted",
};

function categorizeForBucket(r: Risk): { bucket: BucketKey; label: string } {
  const overlay = riskCategoryToOverlay(r.category);
  if (overlay) return { bucket: overlay, label: OVERLAY_LABELS[overlay] };
  // Non-overlay categories: operability / vendor-lock-in / other.
  const fallback = NON_OVERLAY_BUCKETS.find((b) => b.key === r.category);
  if (fallback) {
    return {
      bucket: fallback.key,
      label: r.category_label ?? fallback.label,
    };
  }
  // RiskCategory is a closed set, but be defensive against future additions.
  return { bucket: "other", label: r.category_label ?? "Other" };
}

function collectRisks(
  arch: ArchitectureData,
  drillLevel: DrillLevel,
  selectedContainerId: string | null,
): RiskEntry[] {
  const out: RiskEntry[] = [];

  for (const sys of arch.externalSystems) {
    for (const r of sys.risks ?? []) {
      const { bucket, label } = categorizeForBucket(r);
      out.push({
        bucket,
        bucketLabel: label,
        risk: r,
        nodeId: `external:${sys.id}`,
        nodeLabel: sys.label,
        nodeKind: "external-system",
      });
    }
  }

  const containersInView: Container[] =
    drillLevel === 3
      ? arch.containers.filter((c) => c.id === selectedContainerId)
      : arch.containers;
  const includeComponents = drillLevel === 1 || drillLevel === 3;

  for (const c of containersInView) {
    for (const r of c.risks ?? []) {
      const { bucket, label } = categorizeForBucket(r);
      out.push({
        bucket,
        bucketLabel: label,
        risk: r,
        nodeId: `container:${c.id}`,
        nodeLabel: c.label,
        nodeKind: "container",
      });
    }
    if (includeComponents) {
      for (const cmp of c.components) {
        for (const r of cmp.risks ?? []) {
          const { bucket, label } = categorizeForBucket(r);
          out.push({
            bucket,
            bucketLabel: label,
            risk: r,
            nodeId: `component:${cmp.id}`,
            nodeLabel: cmp.label,
            nodeKind: "component",
          });
        }
      }
    }
  }

  return out;
}

function groupByBucket(
  entries: RiskEntry[],
): { bucket: BucketKey; label: string; entries: RiskEntry[] }[] {
  const buckets = new Map<BucketKey, RiskEntry[]>();
  for (const e of entries) {
    const list = buckets.get(e.bucket);
    if (list) list.push(e);
    else buckets.set(e.bucket, [e]);
  }
  const out: { bucket: BucketKey; label: string; entries: RiskEntry[] }[] = [];
  // Overlay-aligned buckets first, in the canonical OVERLAY_CATEGORIES order
  // so the sheet matches the toolbar order.
  for (const cat of OVERLAY_CATEGORIES) {
    const list = buckets.get(cat);
    if (list && list.length > 0) {
      out.push({ bucket: cat, label: OVERLAY_LABELS[cat], entries: list });
    }
  }
  for (const nb of NON_OVERLAY_BUCKETS) {
    const list = buckets.get(nb.key);
    if (list && list.length > 0) {
      out.push({ bucket: nb.key, label: nb.label, entries: list });
    }
  }
  return out;
}

const KIND_COPY: Record<RiskEntry["nodeKind"], string> = {
  "external-system": "External system",
  container: "Container",
  component: "Component",
};

function SeverityChip({ severity }: { severity: Risk["severity"] }) {
  const cls =
    severity === "critical"
      ? "bg-danger-soft text-danger-ink"
      : severity === "warning"
        ? "bg-warning-soft text-warning-ink"
        : "bg-info-soft text-info-ink";
  return (
    <span
      className={cn(
        "text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-medium shrink-0",
        cls,
      )}
    >
      {severity}
    </span>
  );
}

export function RisksSheet() {
  const { state, dispatch } = useArchViz();
  const arch = state.architecture;

  const groups = useMemo(() => {
    if (!arch) return [];
    return groupByBucket(collectRisks(arch, state.drillLevel, state.selectedContainerId));
  }, [arch, state.drillLevel, state.selectedContainerId]);

  const totalCount = groups.reduce((sum, g) => sum + g.entries.length, 0);

  const open = state.risksSheetOpen;

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.aside
          key="risks-sheet"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 420, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="border-l bg-card shrink-0 relative overflow-hidden"
          style={{ minWidth: 0 }}
        >
          <div className="h-full flex flex-col" style={{ width: 420 }}>
            <div className="shrink-0 border-b bg-card/95 backdrop-blur px-4 py-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  All risks
                </div>
                <h2 className="text-base flex items-center gap-1.5">
                  <ShieldAlert className="h-4 w-4 text-danger" />
                  {totalCount} risk{totalCount === 1 ? "" : "s"} in view
                </h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => dispatch({ type: "CLOSE_RISKS_SHEET" })}
                aria-label="Close risks panel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {totalCount === 0 && (
                <p className="text-sm text-muted-foreground">
                  No risks at this level. Try a different drill level or upload an architecture that
                  records risks on its nodes.
                </p>
              )}
              {groups.map((g) => (
                <div key={g.bucket}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                    {g.label} · {g.entries.length}
                  </div>
                  <div className="space-y-2">
                    {g.entries.map((e, idx) => {
                      const isLegacy = Boolean(e.risk.legacy_origin);
                      const key = `${e.nodeId}:${e.risk.description.slice(0, 32)}:${idx}`;
                      return (
                        <button
                          type="button"
                          key={key}
                          onClick={() =>
                            dispatch({ type: "SELECT_NODE_FOR_RISKS", nodeId: e.nodeId })
                          }
                          className={cn(
                            "w-full text-left text-xs px-3 py-2 transition-colors",
                            isLegacy
                              ? `${LEGACY_ORIGIN_CARD_CLASSES} hover:bg-surface-tint`
                              : "rounded-md border border-danger/30 bg-danger-soft hover:bg-danger/10",
                          )}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                              {KIND_COPY[e.nodeKind]} · {e.nodeLabel}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[9px] uppercase tracking-wider shrink-0",
                                  isLegacy
                                    ? "border-border text-ink-muted"
                                    : "border-danger/30 text-danger",
                                )}
                              >
                                {g.label}
                              </Badge>
                              <SeverityChip severity={e.risk.severity} />
                              {isLegacy && <LegacyOriginChip />}
                            </div>
                          </div>
                          <div
                            className={cn(
                              "prose-sm max-w-none [&_p]:my-0 [&_strong]:font-semibold [&_code]:font-mono [&_code]:text-[11px] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded",
                              isLegacy
                                ? "text-ink [&_code]:bg-surface-tint"
                                : "text-danger-ink [&_code]:bg-danger-soft",
                            )}
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdown(e.risk.description),
                            }}
                          />
                          <div
                            className={cn(
                              "text-[11px] mt-1.5 border-l-2 pl-2",
                              isLegacy
                                ? "border-border text-ink-muted"
                                : RESPONSE_TONE[e.risk.response.kind],
                            )}
                          >
                            <span className="font-medium uppercase tracking-wide text-[9px] mr-1">
                              {RESPONSE_LABEL[e.risk.response.kind]}:
                            </span>
                            <span
                              className="prose-sm max-w-none inline [&_p]:my-0 [&_p]:inline [&_strong]:font-semibold [&_code]:font-mono"
                              dangerouslySetInnerHTML={{
                                __html: renderMarkdown(e.risk.response.text),
                              }}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
